import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Calendar,
  Clock,
  MapPin,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ArrowLeft,
  ShieldCheck,
  CreditCard,
  User,
  Share2,
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';
import {
  fetchV2BookingById,
  sendMeetingStartOtp,
  verifyMeetingStartOtp,
  getBookingCompletionOtp,
  verifyBookingCompletionOtp,
} from '../lib/bookingService';
import { PairUpLogo } from '../components/common/PairUpLogo';

export const BookingDetailPage: React.FC = () => {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();

  const [booking, setBooking] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [startOtpCode, setStartOtpCode] = useState<string>('');
  const [completionOtpCode, setCompletionOtpCode] = useState<string>('');
  const [slideProgress, setSlideProgress] = useState<number>(0);
  const [slideConfirmed, setSlideConfirmed] = useState<boolean>(false);
  const [otpFeedback, setOtpFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [isSubmittingOtp, setIsSubmittingOtp] = useState<boolean>(false);
  const [remainingSeconds, setRemainingSeconds] = useState<number>(0);

  useEffect(() => {
    if (!booking?.meeting_started_at && !booking?.countdown_ends_at) {
      setRemainingSeconds(0);
      return;
    }
    const updateCountdown = () => {
      const durationHours = Number(booking.durationHours || booking.duration_hours || 2);
      const targetMs = booking.countdown_ends_at
        ? new Date(booking.countdown_ends_at).getTime()
        : new Date(booking.meeting_started_at).getTime() + durationHours * 3600 * 1000;
      const diff = Math.max(0, Math.floor((targetMs - Date.now()) / 1000));
      setRemainingSeconds(diff);
    };
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    return () => clearInterval(timer);
  }, [booking?.meeting_started_at, booking?.countdown_ends_at, booking?.durationHours, booking?.duration_hours]);

  const formatCountdown = (totalSec: number) => {
    const hrs = Math.floor(totalSec / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handleRequestStartOtp = async () => {
    if (!booking?.id && !bookingId) return;
    setIsSubmittingOtp(true);
    setOtpFeedback(null);
    const res = await sendMeetingStartOtp(String(booking?.id || bookingId));
    setIsSubmittingOtp(false);
    setOtpFeedback({
      type: res.success ? 'success' : 'error',
      message: res.message || res.error || 'Unable to send Meeting Start OTP.',
    });
  };

  const handleVerifyStartOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!booking?.id && !bookingId) return;
    setIsSubmittingOtp(true);
    setOtpFeedback(null);
    const res = await verifyMeetingStartOtp(String(booking?.id || bookingId), startOtpCode);
    setIsSubmittingOtp(false);
    if (res.success) {
      setBooking((prev: any) => ({
        ...prev,
        ...(res.booking || {}),
        status: 'IN_PROGRESS',
        meeting_started_at: res.meetingStartedAt || new Date().toISOString(),
        countdown_ends_at: res.countdownEndsAt,
      }));
      setStartOtpCode('');
    }
    setOtpFeedback({
      type: res.success ? 'success' : 'error',
      message: res.message || res.error || 'Failed to verify Meeting Start OTP.',
    });
  };

  const handleSlideCompleteChange = async (val: number) => {
    setSlideProgress(val);
    if (val >= 95 && !slideConfirmed) {
      setSlideProgress(100);
      setSlideConfirmed(true);
      if (booking?.id || bookingId) {
        const res = await getBookingCompletionOtp(String(booking?.id || bookingId), auth.currentUser?.uid);
        setOtpFeedback({
          type: res.success ? 'success' : 'error',
          message:
            res.message ||
            res.error ||
            "Meeting Completion OTP sent via APITXT SMS to the client's registered mobile number.",
        });
      }
    }
  };

  const handleVerifyCompletionOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if ((!booking?.id && !bookingId) || !auth.currentUser?.uid) return;
    setIsSubmittingOtp(true);
    setOtpFeedback(null);
    const res = await verifyBookingCompletionOtp(
      String(booking?.id || bookingId),
      auth.currentUser.uid,
      completionOtpCode
    );
    setIsSubmittingOtp(false);
    if (res.success) {
      setBooking((prev: any) => ({
        ...prev,
        ...(res.booking || {}),
        status: 'COMPLETED',
        completed_at: new Date().toISOString(),
      }));
      setCompletionOtpCode('');
    }
    setOtpFeedback({
      type: res.success ? 'success' : 'error',
      message: res.success
        ? `Meeting Successful! ${res.message || 'Booking status updated to COMPLETED and companion payout credited.'}`
        : (res.error || 'Failed to verify Meeting Completion OTP.'),
    });
  };

  useEffect(() => {
    const loadBooking = async () => {
      if (!bookingId) return;
      setIsLoading(true);

      // 1. Try Firestore first
      try {
        const docRef = doc(db, 'bookings', bookingId);
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          setBooking({ id: snap.id, ...snap.data() });
          setIsLoading(false);
          return;
        }
      } catch (err) {
        console.warn('Firestore booking get error:', err);
      }

      // 2. Try backend API fallback
      try {
        const res = await fetchV2BookingById(bookingId);
        if (res.success && res.booking) {
          setBooking(res.booking);
        }
      } catch (e) {
        console.warn('API booking get error:', e);
      } finally {
        setIsLoading(false);
      }
    };

    loadBooking();
  }, [bookingId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="text-center space-y-3">
          <div className="w-8 h-8 border-3 border-pink-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-xs font-bold text-slate-500">Retrieving booking details...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-xl max-w-md w-full text-center space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8" />
          </div>
          <h2 className="text-xl font-black text-slate-900">Booking Not Found</h2>
          <p className="text-xs text-slate-500">
            We could not find booking #{bookingId}. It may have been deleted or the ID is incorrect.
          </p>
          <div className="pt-2">
            <Link
              to="/my-bookings"
              className="px-4 py-2.5 bg-slate-900 text-white font-bold text-xs rounded-xl shadow-xs inline-block"
            >
              Back to My Bookings
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const s = (booking.status || 'pending').toLowerCase();
  const isConfirmed = s.includes('confirmed') || s.includes('accepted');
  const isPending = s.includes('pending');
  const isCompleted = s.includes('completed');
  const isCancelled = s.includes('cancelled') || s.includes('declined');

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col selection:bg-pink-500 selection:text-white">
      {/* Top Header */}
      <header className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-500 hover:text-slate-900 transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Link to="/home" className="flex items-center gap-2">
            <PairUpLogo className="w-10 h-10 shrink-0" />
            <span className="text-lg font-black tracking-tight text-slate-900">
              PairUp<span className="text-pink-600">Today</span>
            </span>
          </Link>
        </div>

        <Link
          to="/my-bookings"
          className="text-xs font-bold text-pink-600 hover:text-pink-700 bg-pink-50 px-3 py-1.5 rounded-full"
        >
          All Bookings
        </Link>
      </header>

      {/* Main Container */}
      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6 w-full">
        {/* Status Card */}
        <div className="bg-white p-6 sm:p-8 rounded-3xl border border-slate-200 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-slate-100">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs font-black text-pink-600 bg-pink-50 px-2 py-0.5 rounded border border-pink-200">
                  #{booking.id || bookingId}
                </span>
                <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
                  Booking Details
                </h1>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Created on {booking.date || booking.booking_date || 'Date unavailable'}
              </p>
            </div>

            {/* Status Pill */}
            {isConfirmed && (
              <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-black rounded-full uppercase border border-emerald-300 self-start sm:self-auto">
                Confirmed
              </span>
            )}
            {isPending && (
              <span className="px-3 py-1 bg-amber-100 text-amber-800 text-xs font-black rounded-full uppercase border border-amber-300 self-start sm:self-auto">
                Pending Confirmation
              </span>
            )}
            {isCompleted && (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-black rounded-full uppercase border border-blue-300 self-start sm:self-auto">
                Completed
              </span>
            )}
            {isCancelled && (
              <span className="px-3 py-1 bg-rose-100 text-rose-800 text-xs font-black rounded-full uppercase border border-rose-300 self-start sm:self-auto">
                Cancelled
              </span>
            )}
          </div>

          {/* Companion Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left: Companion Info */}
            <div className="space-y-4">
              <span className="text-[11px] font-bold uppercase text-slate-400 block tracking-wider">
                Assigned Companion Host
              </span>

              <div className="flex items-center gap-4 p-4 rounded-2xl bg-slate-50 border border-slate-200">
                <div className="w-16 h-16 rounded-2xl overflow-hidden border border-slate-200 shrink-0 bg-slate-100">
                  <img
                    src={
                      booking.companionImage ||
                      booking.companion_image ||
                      'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=400&q=80'
                    }
                    alt={booking.companionName || booking.companion_name}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="space-y-1">
                  <h3 className="text-base font-black text-slate-900">
                    {booking.companionName || booking.companion_name || 'Companion Host'}
                  </h3>
                  <div className="flex items-center gap-1 text-xs text-slate-500">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                    <span>{booking.city || 'Kolkata'}</span>
                  </div>
                  <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-2 py-0.5 rounded-full inline-block">
                    ✓ Identity Verified
                  </span>
                </div>
              </div>

              {/* Schedule Info */}
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3 text-xs">
                <div className="flex items-center gap-2 text-slate-700">
                  <Calendar className="w-4 h-4 text-pink-600 shrink-0" />
                  <span className="font-bold">Date:</span>
                  <span>{booking.date || booking.booking_date}</span>
                </div>

                <div className="flex items-center gap-2 text-slate-700">
                  <Clock className="w-4 h-4 text-pink-600 shrink-0" />
                  <span className="font-bold">Time & Duration:</span>
                  <span>{booking.time || booking.start_time} ({booking.durationHours || booking.duration_hours || 2} Hours)</span>
                </div>

                <div className="flex items-center gap-2 text-slate-700">
                  <MapPin className="w-4 h-4 text-pink-600 shrink-0" />
                  <span className="font-bold">Location:</span>
                  <span>{booking.meetingLocation || booking.location_name}</span>
                </div>
              </div>
            </div>

            {/* Right: Payment & Security OTP */}
            <div className="space-y-4">
              <span className="text-[11px] font-bold uppercase text-slate-400 block tracking-wider">
                Payment Summary & OTP
              </span>

              {/* Price Details */}
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-2 text-xs">
                <div className="flex justify-between text-slate-600">
                  <span>Occasion / Service</span>
                  <span className="font-bold text-slate-900">{booking.occasion || 'Not recorded'}</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Payment Status</span>
                  <span className="font-bold text-emerald-700">
                    {String(booking.payment_status || booking.paymentStatus || 'UNKNOWN').toUpperCase()}
                  </span>
                </div>
                <div className="pt-2 border-t border-slate-200 flex justify-between text-sm font-black text-slate-900">
                  <span>Total Amount Paid</span>
                  <span className="font-mono text-pink-600">
                    {typeof (booking.totalAmount ?? booking.gross_amount) === 'number'
                      ? `₹${Number(booking.totalAmount ?? booking.gross_amount).toLocaleString('en-IN')}`
                      : 'Amount unavailable'}
                  </span>
                </div>
              </div>

              {/* Safety Shield */}
              <div className="p-3.5 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-center gap-2.5 text-[11px] text-emerald-900">
                <ShieldCheck className="w-5 h-5 text-emerald-600 shrink-0" />
                <span>
                  Protected by PairUpToday Meetup Verification & Trust Guarantee.
                </span>
              </div>

              {/* Meeting Start OTP & Countdown + Slide-to-Complete Meeting Completion OTP */}
              {!isCancelled && !isCompleted && (
                <div className="p-4 rounded-2xl bg-slate-900 text-white space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-extrabold text-pink-400 uppercase tracking-wider">
                      Let&apos;s Start Meeting &amp; Verification
                    </span>
                    {s.includes('in_progress') && (
                      <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 text-[10px] font-mono font-bold">
                        Countdown: {formatCountdown(remainingSeconds)}
                      </span>
                    )}
                  </div>

                  {otpFeedback && (
                    <div
                      className={`p-3 rounded-xl text-xs font-semibold ${
                        otpFeedback.type === 'success'
                          ? 'bg-emerald-950/90 border border-emerald-700 text-emerald-200'
                          : 'bg-rose-950/90 border border-rose-700 text-rose-200'
                      }`}
                    >
                      {otpFeedback.message}
                    </div>
                  )}

                  {!s.includes('in_progress') ? (
                    <div className="space-y-3">
                      <button
                        type="button"
                        disabled={isSubmittingOtp}
                        onClick={handleRequestStartOtp}
                        className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-md transition-all cursor-pointer disabled:opacity-50"
                      >
                        Arrived — Let&apos;s Start Meeting (Send Start OTP via APITXT)
                      </button>

                      <form onSubmit={handleVerifyStartOtp} className="space-y-2">
                        <label className="block text-[11px] font-bold text-slate-300">
                          Enter Your Meeting Start OTP
                        </label>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            inputMode="numeric"
                            maxLength={6}
                            value={startOtpCode}
                            onChange={(e) => setStartOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            placeholder="6-digit Start OTP"
                            className="flex-1 px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono text-xs tracking-widest focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          />
                          <button
                            type="submit"
                            disabled={isSubmittingOtp || startOtpCode.length !== 6}
                            className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs cursor-pointer disabled:opacity-50"
                          >
                            Verify &amp; Start
                          </button>
                        </div>
                      </form>
                    </div>
                  ) : (
                    <div className="space-y-3 pt-1">
                      <div className="p-3 rounded-xl bg-slate-950 border border-slate-800 flex items-center justify-between">
                        <span className="text-xs text-slate-300 font-semibold">Live Meeting Countdown</span>
                        <span className="text-base font-black font-mono text-emerald-400">
                          {formatCountdown(remainingSeconds)}
                        </span>
                      </div>

                      <div className="space-y-1.5">
                        <label className="block text-[11px] font-bold text-pink-300">
                          {slideConfirmed
                            ? '✓ Slide Confirmed — Enter Meeting Completion OTP Below'
                            : 'Slide to Complete Meeting →'}
                        </label>
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={slideProgress}
                          disabled={slideConfirmed}
                          onChange={(e) => void handleSlideCompleteChange(Number(e.target.value))}
                          className="w-full accent-pink-500 cursor-pointer"
                        />
                      </div>

                      {slideConfirmed && (
                        <form onSubmit={handleVerifyCompletionOtp} className="space-y-2 pt-1">
                          <label className="block text-[11px] font-bold text-slate-300">
                            Enter 6-Digit Meeting Completion OTP
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              inputMode="numeric"
                              maxLength={6}
                              value={completionOtpCode}
                              onChange={(e) => setCompletionOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              placeholder="6-digit Completion OTP"
                              className="flex-1 px-3 py-2 rounded-xl bg-slate-950 border border-slate-700 text-white font-mono text-xs tracking-widest focus:outline-none focus:ring-2 focus:ring-pink-500"
                            />
                            <button
                              type="submit"
                              disabled={isSubmittingOtp || completionOtpCode.length !== 6}
                              className="px-4 py-2 rounded-xl bg-pink-600 hover:bg-pink-500 text-white font-bold text-xs cursor-pointer disabled:opacity-50"
                            >
                              Complete Meeting
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 text-center text-xs text-slate-400 border-t border-slate-200 bg-white">
        PairUpToday Booking Receipt System
      </footer>
    </div>
  );
};

export default BookingDetailPage;
